import * as React from 'react'
import { ThemeProvider, useTheme } from 'styled-components'
import { customerIndex, RansackQuery } from '../../../../services/models/customers.service'
import { StoreProps } from '../../../../services/stores/casting.store'
import { I18nService } from '../../../../services/utils/i18n.service'
import { onlyUniqueByTransform } from '../../../../services/utils/lodash.service'
import LoadingComponent from '../../../shared/loading.component'
import SelectableListComponent from '../../../shared/selectable-list.component'
import { NoResult } from '../casting-styled.component'
import HomonymTagComponent from '../../../shared/homonym-tag.component'
import { useDebouncer } from '../../../../hooks/use-debouncer'
import { SuppliersCustomerForCastingProps } from '../../../../services/json-schemas/serializers/suppliers-customer-for-casting-props'
import InputTextComponent from '../../../shared/input-text.component'

const i18nScoped = new I18nService(require('./casting-backoffice-customer-picker.component.yml'))

interface Props extends StoreProps {
  afterSelect?: () => void
}

export default function CastingBackofficeCustomerPickerComponent ({ store, data: { customer, offeredService }, afterSelect }: Props) {
  const theme = useTheme()

  const filterCustomerMarket = React.useMemo<RansackQuery>(() => {
    // Employee allowed to create casting for customers (see usage of this component in CastingCustomerComponent) are strictly limited to the supplier's customers.
    if (window.currentUser.type === 'Employee') return { 'q[market_type_eq]': 'Supplier' }

    // Operators of a supplier may be allowed by the marketplace to create castings / events / quotations for customers of the marketplace (undefined = no filter).
    // But depending of the UI, the customer may be selected before or after the offered_service.
    // -> If an offered service has been selected, the customer being picked must be of the same market.
    // -> If the customer is pciked first, the store.market will be updated, so offered_services are loaded from customer.market's catalog.
    if (offeredService) return { 'q[market_type_eq]': offeredService.market_type, 'q[market_id_eq]': offeredService.market_id }
    
    return {}
  }, [offeredService])

  const [ransackQuery, setRansackQuery] = React.useState<RansackQuery>({ ...filterCustomerMarket, 'q[sortable_name_or_reversed_sortable_name_cont]': '', page: 1, per_page: 15, scope: 'active' })
  const [customers, setCustomers] = React.useState<StoreProps['data']['customer'][]>([])
  const [loading, setLoading] = React.useState(true)
  const [hasMore, setHasMore] = React.useState(false)

  const execQuery = React.useCallback(async (fetchMore = false) => {
    setLoading(true)
    
    setRansackQuery(ransackQuery => {
      const nextQuery = { ...ransackQuery, page: fetchMore ? ransackQuery.page + 1 : 1 }

      void customerIndex(nextQuery, 'index_casting_form').then(fetched => {
        setCustomers(customers => fetchMore ? customers.concat(fetched).filter(onlyUniqueByTransform(_ => _.id)) : fetched)
        setHasMore(fetched.length === nextQuery.per_page)
        setLoading(false)
      })

      return nextQuery
    })
  }, [])

  const setCustomerAndFetchMarket = React.useCallback(async (customer: SuppliersCustomerForCastingProps) => {
    if (!await store.setBackofficeCustomerAndFetchMarket(customer)) return
    store.set({ employeeAlreadyWorkedForSelectedCustomer: window.currentUser.type === 'Employee' })
    if (afterSelect) afterSelect()
    else store.nextFocus({ closeOnMobile: true })
  }, [afterSelect, store])

  const debouncer = useDebouncer(1000)

  // eslint-disable-next-line react-hooks/exhaustive-deps
  React.useEffect(() => { void execQuery() }, [])

  return <React.Fragment>
    <InputTextComponent
      id="js-test-find-customer-by-name"
      placeholder={ i18nScoped.t('find_by_name') }
      trimOnBlur={ false }
      value={ ransackQuery['q[sortable_name_or_reversed_sortable_name_cont]'] }
      onChange={ val => {
        setRansackQuery({ ...ransackQuery, 'q[sortable_name_or_reversed_sortable_name_cont]': val })
        debouncer(() => execQuery())
      } } />

    <div style={{ maxHeight: '21em', overflowY: 'auto' }}>
      <ThemeProvider theme={{ ...theme, selectableListOnComputerWithOuterBorder: false }}>
        <SelectableListComponent
          name="customer"
          data={ customers }
          value={ customer }
          formatter={ c => <React.Fragment>
            <div>
              { c.full_name }&nbsp;
              { c.homonym && <HomonymTagComponent className='u-mls' /> }&nbsp;
              { c.marketplace && window.currentUser.market_type === 'Supplier' && <span className="label">{ c.marketplace.name }</span> }
            </div>

            { c.homonym && <div className="u-txt--S">{ c.email }</div> }
          </React.Fragment> }
          onChange={ setCustomerAndFetchMarket } />
      </ThemeProvider>

      { loading && <LoadingComponent align='center' presentation='inline' /> }
      { !loading && customers.length === 0 && <NoResult>{ i18nScoped.t('no_customer_found') }</NoResult> }

      { hasMore && <p className="u-txtCenter">
        <a onClick={ () => void execQuery(true) }>{ i18nScoped.t('see_more') }</a>
      </p> }
    </div>
  </React.Fragment>
}
